import { useTranslation } from "react-i18next";
import {
  BaseAssignmentResponseDto,
  BaseErrorResponseDto,
  EditAssignmentRequestBody,
} from "@packages/api/madlenAPI.schemas";
import { UseMutationResult } from "@tanstack/react-query";
import { BodyType, ErrorType } from "../../../axiosConfig";
import { editAssignment } from "@packages/api/madlenAPI";
import AssignmentCard from "./AssignmentCard";

interface AllAssignmentsListProps {
  assignments: BaseAssignmentResponseDto[];
  isLoading: boolean;
  error: Error | undefined;
  updateAssignment: UseMutationResult<
    Awaited<ReturnType<typeof editAssignment>>,
    ErrorType<BaseErrorResponseDto>,
    { assignmentId: number; data: BodyType<EditAssignmentRequestBody> },
    unknown
  >;
}

const AllAssignmentsList = ({
  assignments,
  isLoading,
  error,
  updateAssignment,
}: AllAssignmentsListProps) => {
  const { t } = useTranslation();

  const handleUpdateName = (id: number, newName: string) => {
    if (updateAssignment) {
      updateAssignment.mutateAsync({
        assignmentId: id,
        data: { name: newName },
      });
    }
  };

  if (isLoading && assignments.length === 0) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {[...Array(4)].map((_, i) => (
          <div
            key={i}
            className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6 animate-pulse"
          >
            <div className="h-6 bg-gray-200 dark:bg-gray-700 rounded w-3/4 mb-4"></div>
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded"></div>
              <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded"></div>
              <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded"></div>
              <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded"></div>
            </div>
            <div className="pt-2 border-t dark:border-gray-700">
              <div className="flex gap-2 mb-2">
                {[...Array(3)].map((_, j) => (
                  <div
                    key={j}
                    className="h-6 w-20 bg-gray-200 dark:bg-gray-700 rounded-full"
                  ></div>
                ))}
              </div>
              <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-32"></div>
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-red-500 dark:text-red-400">
        {t("assignments.error", { message: error.message })}
      </div>
    );
  }

  return (
    <div className="flex flex-col">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {assignments?.map((assignment) => (
          <AssignmentCard
            key={assignment.id}
            assignment={assignment}
            onUpdateName={handleUpdateName}
          />
        ))}
      </div>
      {isLoading && (
        <div className="flex justify-center mt-4">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      )}
    </div>
  );
};

export default AllAssignmentsList;
